/**
 * WorkLogix Phase 1 — Department-Allocated PIN Time Clock: the SINGLE shared
 * contract for the time-clock feature slice (plan-v5 + the plan-v6 offline
 * addendum). Pure and dependency-free (no Nest, no pg, no React) so the API, the
 * kiosk PWA and the supervisor SPA import the SAME types and the SAME
 * reconstruction logic instead of re-deriving them.
 *
 * ── The offline model (plan-v6) ──────────────────────────────────────────────
 * The PUNCH is the durable, idempotent, client-timestamped event. A
 * {@link TimeSession} is a DERIVED PROJECTION recomputed from the ordered punch
 * log by the pure {@link reconstructTimeline} fold. That makes late / out-of-order
 * sync deterministic and re-sends a no-op. {@link reconstructTimeline} is the
 * AUTHORITATIVE reconciliation engine used by BOTH the server (persist derived
 * sessions) and the kiosk client (offline optimistic echo) — there is exactly one
 * implementation of the state machine and both sides call it.
 */
/**
 * A derived, persisted time session (the projection of the punch log for one
 * employee). Camel-cased wire shape of the app.time_session row. `open_punch_id`
 * / `close_punch_id` / `reconciled_at` from the plan-v6 amendment are reflected
 * here as optional camelCase fields.
 */
/**
 * Hours after which a single clocked-in stretch is flagged (Michael, 2026-09-03;
 * raised from 10 to 13 on 2026-09-11).
 *
 * DELIBERATELY ABOVE A NORMAL LONG DAY. Plenty of J&J people genuinely work more than
 * ten hours, so at 10 this fired on ordinary shifts — and an alert that cries wolf is
 * one supervisors learn to filter, taking the real forgotten clock-out with it. Past
 * 13 a missed punch is the likeliest explanation, which is the whole point of sending.
 *
 * Its OWN constant, never reused from MAX_PTO_HOURS_PER_DAY / HOLIDAY_HOURS_PER_DAY:
 * a shift ceiling and a PTO day are different ideas that happened to share a number,
 * and tying them together would mean changing one silently changed the other. Raising
 * this to 13 is exactly the change that would have broken a PTO day had they been one.
 */
export const LONG_SHIFT_THRESHOLD_HOURS = 13;
/**
 * Flag a clocked-in stretch that has run past the threshold, or `undefined` when it
 * has not.
 *
 * ABSENT MEANS FINE, matching `earningFlag` and `needsReview` on {@link TimeSession}:
 * the mere presence of the field is the warning, so a caller cannot forget to check a
 * boolean and silently render "no problem".
 *
 * `now` is a PARAMETER, never read inside. An open session's elapsed time grows with
 * the clock, so a hidden `Date.now()` would make this untestable and would give two
 * callers in the same request different answers.
 *
 * Returns undefined for a malformed or reversed timestamp rather than a negative or
 * NaN figure: a clock-skew artefact must not surface as a spurious 10-hour alert to a
 * supervisor.
 */
export function longShiftFlag(clockInAt, clockOutAt, now) {
    const start = Date.parse(clockInAt);
    const end = clockOutAt === undefined ? now.getTime() : Date.parse(clockOutAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return undefined;
    }
    const hours = (end - start) / 3_600_000;
    if (hours <= LONG_SHIFT_THRESHOLD_HOURS) {
        return undefined;
    }
    return {
        hours: Math.round(hours * 100) / 100,
        stillClockedIn: clockOutAt === undefined,
    };
}
/** Parse an ISO string / Date to epoch ms, throwing on an unparseable instant. */
function toEpochMs(instant) {
    const ms = instant instanceof Date ? instant.getTime() : Date.parse(instant);
    if (Number.isNaN(ms)) {
        throw new RangeError(`Invalid timestamp: '${String(instant)}'`);
    }
    return ms;
}
/**
 * Total order over punches: by `punchedAt` (parsed to epoch, so timezone offsets
 * sort correctly), then by `idempotencyKey` as a stable, deterministic tiebreak.
 * Because idempotency keys are unique, this is a TOTAL order — the fold is
 * deterministic regardless of the input array's order.
 */
function comparePunches(a, b) {
    const at = toEpochMs(a.punchedAt);
    const bt = toEpochMs(b.punchedAt);
    if (at !== bt) {
        return at - bt;
    }
    if (a.idempotencyKey < b.idempotencyKey) {
        return -1;
    }
    if (a.idempotencyKey > b.idempotencyKey) {
        return 1;
    }
    return 0;
}
/**
 * The single-step punch state machine (plan-v5 open/close/switch), retained as the
 * optimistic-echo case for the kiosk client:
 *   - no open session          → CLOCK IN  (open a new session here)
 *   - open session, SAME dept  → CLOCK OUT (close it)
 *   - open session, OTHER dept → SWITCH    (close prior + open here; ONE punch is
 *                                            both the close of the old and the open
 *                                            of the new)
 * Pure: returns the resulting step, never mutates its input.
 */
export function resolvePunch(current, punch) {
    if (current === undefined) {
        return {
            kind: 'clocked_in',
            openSession: {
                departmentId: punch.departmentId,
                clockInAt: punch.punchedAt,
                openPunchKey: punch.idempotencyKey,
            },
        };
    }
    const closedSession = {
        departmentId: current.departmentId,
        clockInAt: current.clockInAt,
        clockOutAt: punch.punchedAt,
        openPunchKey: current.openPunchKey,
        closePunchKey: punch.idempotencyKey,
    };
    if (current.departmentId === punch.departmentId) {
        return { kind: 'clocked_out', closedSession };
    }
    return {
        kind: 'switched',
        closedSession,
        openSession: {
            departmentId: punch.departmentId,
            clockInAt: punch.punchedAt,
            openPunchKey: punch.idempotencyKey,
        },
    };
}
/**
 * AUTHORITATIVE reconstruction engine. Folds an employee's whole punch log into a
 * single deterministic timeline:
 *   1. sort by (`punchedAt`, `idempotencyKey`) — a total order, so out-of-order
 *      input yields the SAME timeline as in-order input;
 *   2. fold {@link resolvePunch} across the sorted punches: none→open,
 *      same-dept→close, other-dept→close+open (switch).
 *
 * INVARIANT: the output holds AT MOST ONE open session (only the trailing state
 * can be open) — guaranteed by construction. Used identically by the server
 * (reconcile + persist) and the kiosk client (offline echo).
 */
export function reconstructTimeline(punches) {
    const sorted = [...punches].sort(comparePunches);
    const sessions = [];
    let open;
    for (const punch of sorted) {
        const step = resolvePunch(open, punch);
        if (step.closedSession !== undefined) {
            sessions.push(step.closedSession);
        }
        open = step.openSession;
    }
    return open === undefined ? { sessions } : { sessions, openSession: open };
}
/** Round to 6 decimal places to shed IEEE-754 noise from the division. */
function round6(n) {
    return Math.round(n * 1e6) / 1e6;
}
/**
 * Decimal hours between clock-in and clock-out under the given rounding rule:
 *   - exact       → raw duration in hours;
 *   - nearest_15  → duration rounded to the nearest 15 minutes;
 *   - nearest_6   → duration rounded to the nearest 6 minutes.
 * Half-way durations round UP (ties-away-from-zero via Math.round on non-negative
 * input). Throws when clock-out precedes clock-in (never valid in a timeline).
 */
/**
 * A worked shift shorter than this is treated as a mistake, not as pay.
 *
 * Michael, 2026-09-14: "I've seen and corrected a lot of entries already that are
 * 0-1 minute long, which doesn't make sense." A double-tap on a kiosk, a punch
 * meant for the other direction, or someone clocking in at the wrong station and
 * immediately correcting it all land as a session a few seconds long.
 *
 * THIRTY MINUTES is the line (Michael, 2026-09-19: "that 'too little time' flag needs
 * to be extended from 5 minutes to 30 minutes"). It was five, which caught only the
 * double-taps; at thirty it also catches the punch that was meant for the other
 * direction and the wrong-station correction somebody fixed a quarter of an hour
 * later. It FLAGS, never blocks or deletes — the hours stay exactly as punched and a
 * supervisor decides.
 *
 * ⚠️ THIS WILL FLAG SOME LEGITIMATE SHORT STINTS — stepping in to cover a break for
 * twenty minutes now raises the flag where it did not before. That is the trade
 * Michael asked for: a flag a supervisor dismisses costs a moment, a bogus entry that
 * nobody looks at gets paid.
 *
 * 🔥 DO NOT RAISE {@link BACK_TO_BACK_GAP_MINUTES} TO MATCH. It used to be described
 * as "matching" this constant and the two are now deliberately different: a
 * thirty-minute back-to-back window would badge every lunch break in the company and
 * teach everyone to ignore the column.
 */
export const MIN_PLAUSIBLE_SESSION_MINUTES = 30;
/**
 * Is this session too short to be real? Open sessions are never short — they have
 * not finished — and a negative span is somebody else's error to report.
 */
export function isImplausiblyShortSession(clockInAt, clockOutAt) {
    // '' is an OPEN shift, not a zero-length one — a blank date input sends an empty
    // string and it must never read as "clocked out at the epoch".
    if (clockOutAt === null || clockOutAt === undefined || clockOutAt === '') {
        return false;
    }
    const minutes = (toEpochMs(clockOutAt) - toEpochMs(clockInAt)) / 60_000;
    return minutes >= 0 && minutes < MIN_PLAUSIBLE_SESSION_MINUTES;
}
export function computeHours(clockInAt, clockOutAt, rule) {
    const durationMinutes = (toEpochMs(clockOutAt) - toEpochMs(clockInAt)) / 60_000;
    if (durationMinutes < 0) {
        throw new RangeError('clockOutAt precedes clockInAt');
    }
    let minutes;
    switch (rule) {
        case 'exact':
            minutes = durationMinutes;
            break;
        case 'nearest_15':
            minutes = Math.round(durationMinutes / 15) * 15;
            break;
        case 'nearest_6':
            minutes = Math.round(durationMinutes / 6) * 6;
            break;
    }
    return round6(minutes / 60);
}
/**
 * Format decimal hours as NetSuite's Time Tracking **"H:MM"** duration string
 * (the exact shape the `timebill.hours` field requires — plan-v8 GATE-2 #3):
 *   - `0.25` → `"0:15"`, `2.5` → `"2:30"`, `8` → `"8:00"`, `0` → `"0:00"`;
 *   - `1.8667` → `"1:52"` (112.002 min → 112 min);
 *   - the whole duration is rounded to the NEAREST WHOLE MINUTE, ties AWAY from
 *     zero (`Math.round` on a non-negative value), so no sub-minute residue ever
 *     reaches payroll;
 *   - `H` is un-padded (no leading zero); `MM` is always two digits, zero-padded;
 *   - MINUTE ROLLOVER carries into the hour — a duration that rounds to 60
 *     minutes (e.g. `0.999722` h ≈ 59.98 min → 60) becomes `"1:00"`, never
 *     `"0:60"`.
 *
 * THROWS on a negative, `NaN` or non-finite input: this string is the pay
 * quantity, and a silently-coerced garbage value is wrong pay. Callers pass an
 * already-rounded {@link computeHours} result; this function's own rounding only
 * collapses floating-point residue to a clean minute.
 */
export function hoursToNetSuiteString(decimalHours) {
    if (!Number.isFinite(decimalHours) || decimalHours < 0) {
        throw new RangeError(`hoursToNetSuiteString: decimalHours must be a finite, non-negative number (got ${String(decimalHours)})`);
    }
    // Round to whole minutes (ties away from zero — input is non-negative).
    const totalMinutes = Math.round(decimalHours * 60);
    // Carry a rounded-up 60th minute into the next hour so MM is always < 60.
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes - hours * 60;
    return `${String(hours)}:${String(minutes).padStart(2, '0')}`;
}
/**
 * Does a supervisor's approval survive a timeline rebuild?
 *
 * 🔥 THE RULE BEHIND TWO BUGS AT ONCE (Michael, 2026-09-16).
 *
 * `reconstructTimeline` is a projection: every unposted session is DELETED and rebuilt
 * from the punch log whenever that employee punches again. Carrying nothing across
 * meant the next morning's clock-in silently un-approved everything a supervisor had
 * signed off ("a lot of the entries we thought were approved and posted are showing as
 * unapproved"), and because the poster selects on `approved_at IS NOT NULL`, a session
 * un-approved between approving and posting dropped out of the batch while the post
 * still reported success — "WLX says time was posted to NetSuite, but that time entry
 * never made it".
 *
 * The opposite case is the second half of the rule, and it is the SAME rule: "If
 * someone makes a change to an approved time entry, that entry needs to fall back to
 * unapproved and needs to be approved again." A rebuild that MOVES the session is such
 * a change, so the approval must not survive it.
 *
 * Compared as INSTANTS, not strings: the prior value comes back from Postgres and the
 * rebuilt one is built in memory, so the same moment can be spelled differently
 * ('...T12:00:00Z' vs '...T12:00:00.000Z') and a string compare would drop every
 * approval it was meant to keep.
 */
export function approvalSurvivesRebuild(prior, rebuilt) {
    const sameInstant = (a, b) => {
        if (a === null || b === null) {
            // An open session and a closed one are not the same span. Both null IS a match:
            // a still-running shift that was approved keeps its approval.
            return a === b;
        }
        const left = Date.parse(a);
        const right = Date.parse(b);
        // An unparseable timestamp must not read as "unchanged" — fail towards
        // re-approval, which costs a click; the other way costs an unreviewed payment.
        return !Number.isNaN(left) && !Number.isNaN(right) && left === right;
    };
    return sameInstant(prior.clockInAt, rebuilt.clockInAt) && sameInstant(prior.clockOutAt, rebuilt.clockOutAt);
}
/**
 * Parse a loosely-typed time of day into minutes past midnight, or null.
 *
 * Michael, 2026-09-17: "I also don't like the way the times are edited too. It's a
 * really frustrating UI that uses scrolling to pick dates and times. Can you make that
 * easier to use and less frustrating?"
 *
 * The native `datetime-local` control makes you spin a wheel to reach a value you
 * already know. On a timecard you almost always DO know it — the punch was at 7:05 —
 * so the fastest input is the one that lets you type it the way you would say it.
 *
 * Accepts what people actually type:
 *   `7:05a`  `7:05 AM`  `705a`  `0705`  `7:05`  `19:05`  `7a`  `7 am`  `12:00am`
 *
 * 🔥 THE 12-HOUR EDGE CASES ARE THE POINT. 12:30am is 00:30 and 12:30pm is 12:30 —
 * naive `h % 12` handling gets one of them wrong, and on a timecard that is a
 * twelve-hour error in somebody's pay. Both are tested.
 *
 * BARE numbers are read as 24-HOUR (`0705` → 07:05, `1900` → 19:00). A bare `7` is
 * 07:00, not 7pm: a clock-in is far more often in the morning, and a supervisor who
 * means the evening has the `p` suffix available.
 *
 * Returns null for anything it cannot read, rather than guessing — the caller keeps
 * the user's text and shows it as invalid instead of silently saving a wrong time.
 */
export function parseTimeOfDay(input) {
    const text = input.trim().toLowerCase().replace(/\./g, '');
    if (text === '') {
        return null;
    }
    const match = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/.exec(text);
    if (match === null) {
        return null;
    }
    const rawHour = Number.parseInt(match[1], 10);
    const meridiem = match[3];
    // `705` / `1930`: no separator, no meridiem — the leading digits are the hour.
    let hour = rawHour;
    let minute = match[2] === undefined ? 0 : Number.parseInt(match[2], 10);
    if (match[2] === undefined && meridiem === undefined && match[1].length > 2) {
        const digits = match[1];
        hour = Number.parseInt(digits.slice(0, digits.length - 2), 10);
        minute = Number.parseInt(digits.slice(digits.length - 2), 10);
    }
    if (minute > 59) {
        return null;
    }
    if (meridiem !== undefined) {
        if (hour < 1 || hour > 12) {
            return null;
        }
        const pm = meridiem.startsWith('p');
        // 12am is 00:xx and 12pm is 12:xx — the two cases a bare `% 12` gets wrong.
        hour = hour === 12 ? (pm ? 12 : 0) : pm ? hour + 12 : hour;
    }
    else if (hour > 23) {
        return null;
    }
    return hour * 60 + minute;
}
/** Minutes past midnight → `HH:mm`, the value a date input round-trips. */
export function formatTimeOfDay(minutes) {
    const h = Math.floor(minutes / 60) % 24;
    return `${String(h).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}
//# sourceMappingURL=time.js.map